import { fabric } from 'fabric';
import type Table from '../objects/Table';
import ControlBase from '../fabric/ControlBase';

export interface Point {
  x: number;
  y: number;
}

const WIDTH = 23;
const HEIGHT = 16;
const MIN_DIMENSION = 75;

class TableVectorDragControl extends ControlBase {
  // Name of the action the control is used for.
  actionName: string;
  // Whether this control lies on a row or column
  draggingVertical: boolean;
  // Index of vector in table
  index: number;
  // fill color of dots in control object
  dotColor: string;
  // fill color of control
  controlColor: string;
  grabbing: boolean;

  constructor({
    index,
    draggingVertical = true,
  }: {
    index: number;
    draggingVertical: boolean;
  }) {
    const offsetX = draggingVertical ? -20 : 0;
    const offsetY = draggingVertical ? 0 : -20;

    super({
      offsetX,
      offsetY,
    });

    this.index = index;
    this.draggingVertical = draggingVertical;
    this.actionName = `Drag ${draggingVertical ? 'row' : 'column'}`;
    this.cursorStyle = 'grab';

    if (this.draggingVertical) {
      this.sizeX = this.touchSizeX = HEIGHT;
      this.sizeY = this.touchSizeY = WIDTH;
    } else {
      this.sizeX = this.touchSizeX = WIDTH;
      this.sizeY = this.touchSizeY = HEIGHT;
    }

    // TODO figure out how to get grabbing cursor on mouse down
    this.cursorStyle = 'grab';
  }

  getMouseDownHandler(): fabric.ControlMouseEventHandler {
    return () => {
      return false;
    };
  }

  getMouseUpHandler() {
    return (e: MouseEvent, transformData: fabric.Transform) => {
      this.cursorStyle = 'grab';
      const object = transformData.target as Table;
      const draggingVertical = object.__corner?.[0] === 'r';
      let end;
      if (
        object.draggedVectorEndIndex &&
        object.draggedVectorEndIndex > this.index
      ) {
        end = object.draggedVectorEndIndex - 1;
      } else {
        end = object.draggedVectorEndIndex;
      }
      if (object.updateVectorOrder(this.index, end, draggingVertical)) {
        object.canvas.controller.undoManager.completeUndoGroup();
      }
      this.index = end;
      object.draggedVectorTop = null;
      object.draggedVectorLeft = null;
      object.draggedVectorEndIndex = null;
      this.dotColor = '#666';
      this.controlColor = 'white';

      return false;
    };
  }

  // TODO this feels like it's doing more work than it needs to to get the relative
  // -0.5 to 0.5 x and y values
  positionHandler(
    dim: { x: number; y: number },
    finalMatrix: unknown,
    fabricObject: Table,
    currentControl: fabric.Control
  ): fabric.Point {
    const zoom = fabricObject.canvas?.getZoom() || 1;
    if (this.visible) {
      this.setVisibility(
        Math.min(
          fabricObject.width * fabricObject.scaleX,
          fabricObject.height * fabricObject.scaleY
        ) >
          MIN_DIMENSION / zoom
      );
    }
    const rect = fabricObject.getVectorRect({
      index: this.index,
      isRow: this.draggingVertical,
      scaled: true,
    });

    if (rect) {
      const { left, top, width, height } = rect;
      let controlLeft = left;
      let controlTop = top;

      if (this.draggingVertical) {
        controlTop += fabricObject.strokeWidth / 2;
      } else {
        controlLeft += fabricObject.strokeWidth / 2;
      }

      // If there is a drag happening, adjust vector left or top appropriately
      if (this.draggingVertical && fabricObject.draggedVectorTop) {
        controlTop = fabricObject.draggedVectorTop * fabricObject.scaleY;
      } else if (!this.draggingVertical && fabricObject.draggedVectorLeft) {
        controlLeft = fabricObject.draggedVectorLeft * fabricObject.scaleX;
      }

      const adjustedPointer = fabric.util.transformPoint(
        {
          x: controlLeft + width / 2,
          y: controlTop + height / 2,
        } as fabric.Point,
        fabric.util.invertTransform(fabricObject.calcOwnMatrix())
      );

      const tableDim = fabricObject._getNonTransformedDimensions();
      if (this.draggingVertical && fabricObject.draggedVectorTop) {
        this.x = -0.5;
        this.y =
          (fabricObject.draggedVectorTop + height / fabricObject.scaleY / 2) /
          tableDim.y;
      } else if (!this.draggingVertical && fabricObject.draggedVectorLeft) {
        this.x =
          (fabricObject.draggedVectorLeft + width / fabricObject.scaleX / 2) /
          tableDim.x;
        this.y = -0.5;
      } else {
        this.x = this.draggingVertical ? -0.5 : adjustedPointer.x / tableDim.x;
        this.y = this.draggingVertical ? adjustedPointer.y / tableDim.y : -0.5;
      }
    }

    return super.positionHandler(
      dim,
      finalMatrix,
      fabricObject,
      currentControl
    );
  }

  getActionHandler(
    eventData: MouseEvent,
    fabricObject: Table
  ): fabric.ControlMouseEventHandler {
    return (eventData, transform, x, y) => {
      // Set control color values
      this.dotColor = 'white';
      this.controlColor = '#0073CF';

      // Get this control's dimensions
      const object = transform.target as Table;
      const dragVectorKey = this.draggingVertical
        ? 'draggedVectorTop'
        : 'draggedVectorLeft';
      const rect = fabricObject.getVectorRect({
        index: this.index,
        isRow: this.draggingVertical,
        scaled: true,
      });

      if (rect) {
        const { width, height } = rect;
        const dim = object._getTransformedDimensions();
        const localPoint = fabric.controlsUtils.getLocalPoint(
          transform,
          transform.originX,
          transform.originY,
          x,
          y
        );

        // Get point relative to table to determine where vector ends up
        const adjustedPointer = fabric.util.transformPoint(
          { x, y } as fabric.Point,
          fabric.util.invertTransform(object.calcOwnMatrix())
        );

        // Use local 0 -> table dim point to calculate bounds
        const lowerLimit = this.draggingVertical
          ? localPoint.y >= height / 2
          : localPoint.x >= width / 2;
        const upperLimit = this.draggingVertical
          ? localPoint.y + height / 2 <= dim.y
          : localPoint.x + width / 2 <= dim.x;
        // Use table relative dimensions to actually set the vector
        const dragVectorEndPoint = this.draggingVertical
          ? adjustedPointer.y - height / object.scaleY / 2
          : adjustedPointer.x - width / object.scaleX / 2;

        // Vector was dragged passed endds of table, so place vector right at end
        if (!lowerLimit) {
          const dim = object._getNonTransformedDimensions();
          const endPoint = this.draggingVertical ? -dim.y / 2 : -dim.x / 2;
          object.set(dragVectorKey, endPoint);
        } else if (!upperLimit) {
          const dim = object._getNonTransformedDimensions();
          const endPoint = this.draggingVertical
            ? dim.y / 2 - height / object.scaleY
            : dim.x / 2 - width / object.scaleX;
          object.set(dragVectorKey, endPoint);
        } else {
          // Otherwise, just place vector relative to cursor
          object.set(dragVectorKey, dragVectorEndPoint);
        }

        // Set table end vector index to indicate where dragged vector will land
        object.draggedVectorEndIndex = object.getEndVectorIndex(
          this.draggingVertical ? adjustedPointer.y : adjustedPointer.x,
          this.index,
          this.draggingVertical
        );
        return true;
      }
      return false;
    };
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride,
    fabricObject
  ) {
    if (this.isActive(fabricObject)) {
      fabricObject?.canvas?.setCursor('grabbing');
    }
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.fillStyle = this.isActive(fabricObject) ? '#0073CF' : '#FFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
    ctx.shadowBlur = 8;
    ctx.strokeStyle = '#E1E1E1';
    ctx.lineWidth = 1;
    const width = this.draggingVertical ? HEIGHT : WIDTH;
    const height = this.draggingVertical ? WIDTH : HEIGHT;
    const radius = 3;
    left = left - width / 2;
    top = top - height / 2;

    ctx.beginPath();
    ctx.moveTo(left + radius, top);
    // Draw the top line of the rectangle
    ctx.lineTo(left + width - radius, top);
    // Draw the top-right corner of the rectangle
    ctx.arcTo(left + width, top, left + width, top + radius, radius);
    // Draw the right side of the rectangle
    ctx.lineTo(left + width, top + height - radius);
    // Draw the bottom-right corner of the rectangle
    ctx.arcTo(
      left + width,
      top + height,
      left + width - radius,
      top + height,
      radius
    );
    // Draw the bottom side of the rectangle
    ctx.lineTo(left + radius, top + height);
    // Draw the bottom-left corner of the rectangle
    ctx.arcTo(left, top + height, left, top + height - radius, radius);
    // Draw the left side of the rectangle
    ctx.lineTo(left, top + radius);
    // Draw the top-left corner of the rectangle
    ctx.arcTo(left, top, left + radius, top, radius);
    ctx.stroke();
    ctx.fill();

    const dotRadius = 1.2;
    const dotPadding = 2.4;
    const rowCount = this.draggingVertical ? 3 : 2;
    const colCount = this.draggingVertical ? 2 : 3;
    const xOffset = this.draggingVertical
      ? (width * 2) / 5 - dotRadius
      : (width - dotRadius * 2 * 3 - dotPadding * 2) / 2;
    const yOffset = this.draggingVertical
      ? (height - dotRadius * 2 * 3 - dotPadding * 2) / 2
      : (height * 2) / 5 - dotRadius;

    const startX = left + xOffset;
    const startY = top + yOffset;

    for (let i = 0; i < rowCount; i++) {
      for (let j = 0; j < colCount; j++) {
        const rowPadding = this.draggingVertical
          ? j * (width / 3)
          : dotRadius + j * (dotRadius * 2 + dotPadding);
        const colPadding = this.draggingVertical
          ? dotRadius + i * (dotRadius * 2 + dotPadding)
          : i * (height / 3);

        const dotX = startX + rowPadding;
        const dotY = startY + colPadding;
        ctx.beginPath();
        ctx.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI);
        if (this.isActive(fabricObject)) {
          ctx.fillStyle = '#FFF';
        } else if (this.hovered) {
          ctx.fillStyle = '#0073CF';
        } else {
          ctx.fillStyle = '#666';
        }
        ctx.fill();
      }
    }
    ctx.restore();
  }
}
export default TableVectorDragControl;
